import React, { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const COUNTRIES = [
  { code: "+221", flag: "🇸🇳", name: "Sénégal" },
  { code: "+225", flag: "🇨🇮", name: "Côte d'Ivoire" },
  { code: "+223", flag: "🇲🇱", name: "Mali" },
  { code: "+224", flag: "🇬🇳", name: "Guinée" },
  { code: "+226", flag: "🇧🇫", name: "Burkina Faso" },
  { code: "+222", flag: "🇲🇷", name: "Mauritanie" },
  { code: "+227", flag: "🇳🇪", name: "Niger" },
  { code: "+228", flag: "🇹🇬", name: "Togo" },
  { code: "+229", flag: "🇧🇯", name: "Bénin" },
  { code: "+237", flag: "🇨🇲", name: "Cameroun" },
  { code: "+233", flag: "🇬🇭", name: "Ghana" },
  { code: "+234", flag: "🇳🇬", name: "Nigéria" },
  { code: "+33", flag: "🇫🇷", name: "France" },
];

const SORTED = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length);

// Split an E.164 string into { code, local } using the longest matching dial code.
export function splitPhone(e164) {
  if (!e164) return { code: "+221", local: "" };
  const match = SORTED.find((c) => e164.startsWith(c.code));
  if (match) return { code: match.code, local: e164.slice(match.code.length) };
  return { code: "+221", local: e164.replace(/^\+/, "") };
}

// Normalize the local part: keep digits, strip a duplicated dial code and leading zeros.
function normalizeLocal(raw, code) {
  let digits = (raw || "").replace(/[^\d]/g, "");
  const codeDigits = code.replace("+", "");
  // If user re-typed the country code (e.g. "221 77…"), strip it.
  if (digits.startsWith(codeDigits) && digits.length > codeDigits.length) {
    digits = digits.slice(codeDigits.length);
  }
  // Strip leading zeros (national trunk prefix).
  digits = digits.replace(/^0+/, "");
  return digits;
}

export function PhoneInput({ value, onChange, testId = "phone", autoFocus }) {
  const initial = splitPhone(value);
  const [code, setCode] = useState(initial.code);
  const [local, setLocal] = useState(initial.local);

  // Sync when the parent value changes externally (e.g. reset to defaults).
  useEffect(() => {
    const s = splitPhone(value);
    if (s.code !== code || s.local !== local) {
      setCode(s.code);
      setLocal(s.local);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (c, l) => onChange(`${c}${l}`);

  const handleCode = (newCode) => {
    setCode(newCode);
    emit(newCode, local);
  };
  const handleLocal = (e) => {
    const l = normalizeLocal(e.target.value, code);
    setLocal(l);
    emit(code, l);
  };

  const full = `${code} ${local}`.trim();

  return (
    <div>
      <div className="flex gap-2">
        <Select value={code} onValueChange={handleCode}>
          <SelectTrigger
            data-testid={`${testId}-country`}
            className="w-[112px] h-[52px] rounded-xl bg-white border-gray-300 text-base font-semibold"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {COUNTRIES.map((c) => (
              <SelectItem key={c.code} value={c.code} className="text-base">
                <span className="mr-2">{c.flag}</span>
                {c.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input
          data-testid={`${testId}-number`}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          value={local}
          onChange={handleLocal}
          placeholder="77 123 45 67"
          className="flex-1 h-[52px] rounded-xl bg-white border border-gray-300 px-4 text-base outline-none focus:border-tp-green focus:ring-2 focus:ring-tp-green/20"
        />
      </div>
      {local && (
        <p data-testid={`${testId}-preview`} className="text-sm text-tp-muted mt-1.5">
          Numéro : <span className="font-semibold text-tp-ink">{full}</span>
        </p>
      )}
    </div>
  );
}
